import { Link, useNavigate } from "react-router-dom";
import Button from "../Button/Button";
import { useContext, useEffect, useState } from "react";
import TextInput from "../TextInput/TextInput";
import Checkbox from "../Checkbox/Checkbox";
import { validateEmail, validatePass } from "../../utils";
import { AuthContext } from "../../contexts/AuthContext/AuthContext";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import useHomeRoute from "../../hooks/useHomeRoute";
import OtpComponent from "../OtpComponent";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface LoginProps {
  onOtpStateChange?: (showOtp: boolean) => void;
}

const Login = ({ onOtpStateChange }: LoginProps) => {
  const auth = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const navigate = useNavigate();
  const { homeLink } = useHomeRoute();
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Load saved email from localStorage on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  // Save email to localStorage whenever email changes and rememberMe is checked
  useEffect(() => {
    if (rememberMe && email.trim()) {
      localStorage.setItem("rememberedEmail", email.trim());
    }
  }, [email, rememberMe]);

  // Handle remember me checkbox change
  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (checked) {
      // Save email to localStorage when checked
      if (email.trim()) {
        localStorage.setItem("rememberedEmail", email.trim());
      }
    } else {
      // Remove email from localStorage when unchecked
      localStorage.removeItem("rememberedEmail");
    }
  };

  const validateEmailField = (value: string): string => {
    // Must not be empty
    if (!value || !value.trim()) {
      return "Email is required.";
    }

    const trimmedValue = value.trim();

    // Maximum length: 254 characters
    if (trimmedValue.length > 254) {
      return "Please enter a valid email address.";
    }

    // Must be valid email format (contains @, domain, and TLD)
    if (!validateEmail(trimmedValue)) {
      return "Please enter a valid email address.";
    }

    return "";
  };

  const validEmail = validateEmail(email.trim());
  const validPassword = validatePass(password);

  // Notify parent when OTP state changes
  useEffect(() => {
    onOtpStateChange?.(showOtpForm);
  }, [showOtpForm, onOtpStateChange]);

  // Navigate to home after successful login (when user is authenticated and currentUser is loaded)
  useEffect(() => {
    if (auth?.isAuthenticated && currentUser && !showOtpForm && homeLink) {
      navigate(homeLink);
    }
  }, [auth?.isAuthenticated, currentUser, showOtpForm, homeLink, navigate]);

  const handleSubmit = async (resend?: boolean) => {
    setShowError(!resend);

    // Validate email
    const emailValidationError = validateEmailField(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      setShowError(true);
      return;
    }
    setEmailError("");

    if (validEmail && validPassword) {
      setLoading(true);
      const loginResponse = await auth?.signInNew({
        email: email.trim(),
        password: password.trim(),
      });

      if (loginResponse) {
        // Save email to localStorage if remember me is checked
        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email.trim());
        } else {
          localStorage.removeItem("rememberedEmail");
        }
        // Simple logic: if emailVerificationRequired is true, show OTP form
        if (loginResponse.emailVerificationRequired) {
          setShowOtpForm(true);
          if (resend) {
            toast?.openToast("A new code has been sent to your email.", 2000, ToastVariants.Success);
          }
        }
        // If emailVerificationRequired is false, login happens automatically via auth context
      } else {
        toast?.openToast("Please confirm your credentials", 2000, ToastVariants.Error);
      }
      setShowError(false);
      setLoading(false);
    }
  };

  const handleSubmitOTP = async () => {
    setLoading(true);
    setOtpError(false);
    const res = await auth?.authenticateMFA(email.trim(), otpInput);
    if (res) {
      // Save email to localStorage if remember me is checked
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email.trim());
      } else {
        localStorage.removeItem("rememberedEmail");
      }
      setShowOtpForm(false);
      navigate(homeLink);
    } else {
      setOtpError(true);
    }
    setLoading(false);
  };

  const handleResendCode = async () => {
    setOtpInput("");
    setOtpError(false);
    await handleSubmit(true);
  };

  // Show OTP form when emailVerificationRequired is true
  if (showOtpForm) {
    return (
      <div className="w-full">
        <OtpComponent
          value={email.trim()}
          otpInput={otpInput}
          setOtpInput={setOtpInput}
          loading={loading}
          showError={otpError}
          submit={handleSubmitOTP}
          resend={handleResendCode}
        />
      </div>
    );
  }

  return (
    <>
      <h2 className="text-[32px] font-bold text-navy-blue mb-8">Login</h2>
      <div className="w-full">
        <TextInput
          label="Email"
          isRequired
          errorMsg={emailError || "Please enter a valid email address"}
          value={email}
          placeholder="name@example.com"
          isError={!!emailError || (!validEmail && showError)}
          onChangeFunc={e => {
            setEmail(e.target.value);
            // Clear error when user starts typing
            if (emailError) {
              setEmailError("");
            }
          }}
          onBlur={() => {
            const error = validateEmailField(email);
            setEmailError(error);
            // Save email to localStorage on blur if remember me is checked
            if (rememberMe && email.trim()) {
              localStorage.setItem("rememberedEmail", email.trim());
            }
          }}
          isDarkBg={false}
          extraInputClass="bg-transparent mt-2.5"
        />
      </div>
      <div className="w-full">
        <TextInput
          label="Password"
          isPassword
          isRequired
          errorMsg={"Please enter your password"}
          value={password}
          placeholder="Enter password"
          isError={!validPassword && showError}
          onChangeFunc={e => {
            setPassword(e.target.value);
          }}
          isDarkBg={false}
          extraInputClass="bg-transparent mt-2.5"
        />
      </div>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <Checkbox checked={rememberMe} onChange={e => handleRememberMeChange(e.target.checked)} name="rememberMe" />
          <label htmlFor="rememberMe" className="ml-2 font-medium font-inter">
            Remember me
          </label>
        </div>
        <Link to="/forgot-password" className="font-bold font-inter text-link-blue">
          Forgot password?
        </Link>
      </div>
      <div className="w-full mt-5">
        <Button
          text="Login"
          onClick={handleSubmit}
          disabled={(showError && (!validEmail || !validPassword)) || loading}
          variant={loading ? "loading" : "Primary"}
          width="full"
          className="rounded-[20px] h-[50px] !text-[16px] !font-bold !font-inter"
        />
      </div>
      {auth?.errorMessage ? <p className="text-docsigna-orange">{auth?.errorMessage}</p> : null}
    </>
  );
};

export default Login;
